import { motion } from "framer-motion";
import { useState } from "react";

// Approximate world map outline (continents) as a stylized SVG with lat/long ports.
// Coordinates are mapped to a 1000 x 500 viewBox using equirectangular projection helper.
const project = (lat, lon) => {
    const x = (lon + 180) * (1000 / 360);
    const y = (90 - lat) * (500 / 180);
    return [x, y];
};

const PORTS = [
    { id: "sgp", name: "Singapore HQ", lat: 1.29, lon: 103.85, primary: true },
    { id: "rdam", name: "Rotterdam", lat: 51.95, lon: 4.14 },
    { id: "shn", name: "Shanghai", lat: 31.23, lon: 121.47 },
    { id: "hou", name: "Houston", lat: 29.76, lon: -95.36 },
    { id: "dxb", name: "Jebel Ali", lat: 25.01, lon: 55.06 },
    { id: "san", name: "Santos", lat: -23.96, lon: -46.33 },
    { id: "lag", name: "Lagos", lat: 6.45, lon: 3.39 },
    { id: "ham", name: "Hamburg", lat: 53.55, lon: 9.99 },
    { id: "mum", name: "Mumbai", lat: 19.07, lon: 72.87 },
    { id: "syd", name: "Sydney", lat: -33.86, lon: 151.21 },
    { id: "tyo", name: "Tokyo", lat: 35.68, lon: 139.69 },
    { id: "lax", name: "Long Beach", lat: 33.77, lon: -118.19 },
];

// Routes from Singapore HQ
const ROUTES = [
    ["sgp", "rdam"], ["sgp", "shn"], ["sgp", "hou"], ["sgp", "dxb"],
    ["sgp", "san"], ["sgp", "lag"], ["sgp", "ham"], ["sgp", "mum"],
    ["sgp", "syd"], ["sgp", "tyo"], ["sgp", "lax"],
];

// Curved path between two points
const curvePath = (a, b) => {
    const [x1, y1] = a;
    const [x2, y2] = b;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2 - Math.abs(x2 - x1) * 0.18;
    return `M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`;
};

export default function GlobalMap() {
    const [active, setActive] = useState("sgp");
    const portMap = Object.fromEntries(PORTS.map((p) => [p.id, project(p.lat, p.lon)]));

    return (
        <section data-testid="global-map" className="relative py-24 md:py-32 bg-[#040914] border-y border-white/5 overflow-hidden">
            <div className="absolute inset-0 wms-radial pointer-events-none" />

            <div className="wms-container">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div className="max-w-2xl">
                        <span className="wms-overline">Operations — 03</span>
                        <h2 className="mt-5 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight">
                            A live map of where Windermere sails.
                        </h2>
                        <p className="mt-5 text-white/60 text-base md:text-lg leading-relaxed max-w-xl">
                            From Singapore, our fixtures fan out to deep-water hubs across Asia, EMEA and the Americas — coordinated 24/7 by our chartering desk.
                        </p>
                    </div>

                    <div className="grid grid-cols-3 gap-3 md:max-w-md w-full">
                        {[
                            { l: "Hubs", v: "12" },
                            { l: "Lanes", v: "60+" },
                            { l: "Voyages / yr", v: "480" },
                        ].map((s) => (
                            <div key={s.l} className="wms-glass p-4">
                                <div className="font-display text-2xl text-white">{s.v}</div>
                                <div className="text-[10px] mt-1 tracking-[0.28em] uppercase text-white/55">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative border border-white/10 bg-[#040914]">
                    {/* World silhouette */}
                    <svg viewBox="0 0 1000 500" className="w-full h-auto block" preserveAspectRatio="xMidYMid meet">
                        <defs>
                            <radialGradient id="oceanGlow" cx="50%" cy="50%" r="60%">
                                <stop offset="0%" stopColor="#0B1324" stopOpacity="0.9" />
                                <stop offset="100%" stopColor="#040914" stopOpacity="1" />
                            </radialGradient>
                            <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#48CAE4" stopOpacity="0.9" />
                                <stop offset="100%" stopColor="#023E8A" stopOpacity="0.6" />
                            </linearGradient>
                            <pattern id="dots" width="6" height="6" patternUnits="userSpaceOnUse">
                                <circle cx="1" cy="1" r="0.7" fill="#1B2742" />
                            </pattern>
                        </defs>

                        <rect width="1000" height="500" fill="url(#oceanGlow)" />
                        <rect width="1000" height="500" fill="url(#dots)" opacity="0.6" />

                        {/* Stylized continent outlines (simplified) */}
                        <g fill="#0e1a33" stroke="#1B2742" strokeWidth="0.5">
                            {/* North America */}
                            <path d="M120,90 L260,80 L300,140 L260,220 L200,260 L160,210 L120,160 Z" />
                            {/* South America */}
                            <path d="M270,260 L320,260 L340,320 L320,400 L280,420 L260,360 L260,300 Z" />
                            {/* Europe */}
                            <path d="M470,90 L560,90 L580,140 L520,170 L470,150 Z" />
                            {/* Africa */}
                            <path d="M490,180 L580,180 L600,260 L560,360 L520,360 L490,290 Z" />
                            {/* Asia */}
                            <path d="M580,90 L820,90 L860,150 L840,200 L780,230 L700,210 L620,180 L580,140 Z" />
                            {/* Oceania */}
                            <path d="M780,330 L880,330 L900,380 L820,400 L770,370 Z" />
                        </g>

                        {/* Routes */}
                        <g fill="none" stroke="url(#routeGrad)" strokeWidth="1.4">
                            {ROUTES.map(([a, b], i) => (
                                <path
                                    key={`${a}-${b}`}
                                    d={curvePath(portMap[a], portMap[b])}
                                    className="wms-route"
                                    style={{ animationDelay: `${i * 0.4}s`, opacity: active === a || active === b ? 1 : 0.55 }}
                                />
                            ))}
                        </g>

                        {/* Ports */}
                        {PORTS.map((p) => {
                            const [x, y] = portMap[p.id];
                            const isActive = active === p.id;
                            return (
                                <g
                                    key={p.id}
                                    onMouseEnter={() => setActive(p.id)}
                                    className="cursor-pointer"
                                >
                                    {p.primary && (
                                        <circle cx={x} cy={y} r="14" fill="#48CAE4" opacity="0.15">
                                            <animate attributeName="r" values="10;22;10" dur="3s" repeatCount="indefinite" />
                                            <animate attributeName="opacity" values="0.4;0;0.4" dur="3s" repeatCount="indefinite" />
                                        </circle>
                                    )}
                                    <circle
                                        cx={x}
                                        cy={y}
                                        r={p.primary ? 5 : 3.4}
                                        fill={p.primary ? "#48CAE4" : isActive ? "#48CAE4" : "#94A3B8"}
                                        stroke="#040914"
                                        strokeWidth="1.5"
                                    />
                                    <text
                                        x={x + 9}
                                        y={y - 6}
                                        fill={isActive || p.primary ? "#FFFFFF" : "#94A3B8"}
                                        fontSize="9"
                                        fontFamily="Outfit, sans-serif"
                                        letterSpacing="2"
                                        className="uppercase select-none"
                                    >
                                        {p.name}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>

                    {/* Legend overlay */}
                    <div className="absolute left-4 bottom-4 flex items-center gap-5 text-[10px] tracking-[0.28em] uppercase text-white/55 wms-glass px-4 py-2">
                        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#48CAE4]" /> Primary Hub</span>
                        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#94A3B8]" /> Active Port</span>
                    </div>
                </div>

                <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 text-sm text-white/55"
                >
                    Selected hub: <span className="text-white">{PORTS.find((p) => p.id === active)?.name}</span>
                </motion.div>
            </div>
        </section>
    );
}
